/**
 * Pre-onboarding Portal — HR cockpit.
 *
 * Single page at `/app/preboarding` for recruiters to track every candidate
 * sitting between offer-accept and joining-date. KPIs at the top, table of
 * active candidates below, full per-candidate detail dialog with checklist,
 * Day-1 schedule editor, buddy picker, drip log, and magic-link share.
 */
import { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Users, Calendar, Link as LinkIcon, EnvelopeSimple, ClockClockwise, Trash, Plus, Copy, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API, formatApiError } from "../lib/api";
import { DateInput } from "../components/ui/date-input";

const errMsg = (e) => formatApiError(e?.response?.data?.detail) || "Something went wrong";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return iso; } };
const daysUntil = (iso) => {
  try {
    const d = new Date(iso);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    return Math.round((d - today) / 86400000);
  } catch { return null; }
};

const DRIPS = [
  { id: "welcome",          label: "Welcome" },
  { id: "kyc_nudge",        label: "KYC nudge" },
  { id: "buddy_intro",      label: "Buddy intro" },
  { id: "day_one_preview",  label: "Day-1 preview" },
  { id: "tomorrow",         label: "See you tomorrow" },
];

export default function Preboarding() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);     // preboarding doc opened in dialog
  const [employees, setEmployees] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s, e] = await Promise.all([
        api.get("/preboarding"),
        api.get("/preboarding/summary/stats"),
        api.get("/employees"),
      ]);
      setRows(r.data || []);
      setStats(s.data);
      setEmployees(e.data || []);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openDetail = async (row) => {
    try {
      const r = await api.get(`/preboarding/${row.id}`);
      setViewing(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="preboarding-page">
        <header className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold text-zinc-900">Pre-onboarding Portal</h1>
            <p className="text-sm text-zinc-600 mt-1">
              Keep accepted candidates engaged from offer-accept to Day 1. Magic-link checklist, Day-1 schedule, buddy intro, drip emails.
            </p>
          </div>
        </header>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <KPI label="Active"          value={stats?.active ?? "—"} tone="emerald" testid="pb-kpi-active" />
          <KPI label="Upcoming (7d)"   value={stats?.upcoming_7d ?? "—"} tone="amber" testid="pb-kpi-upcoming" />
          <KPI label="Missing buddy"   value={stats?.missing_buddy ?? "—"} tone="rose" testid="pb-kpi-missing-buddy" />
          <KPI label="Missing DOJ"     value={stats?.missing_doj ?? "—"} tone="rose" testid="pb-kpi-missing-doj" />
          <KPI label="Completed"       value={stats?.completed ?? "—"} tone="zinc" testid="pb-kpi-completed" />
          <KPI label="No-shows"        value={stats?.no_show ?? "—"} tone="zinc" testid="pb-kpi-noshows" />
        </div>

        {/* Table */}
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">Candidate</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Joining</th>
                <th className="px-4 py-3 text-left">Buddy</th>
                <th className="px-4 py-3 text-left">Progress</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">
                  No active preboardings yet. The portal auto-initializes when a candidate accepts an offer.
                </td></tr>
              )}
              {rows.map((r) => {
                const done = (r.checklist || []).filter((c) => c.status === "done").length;
                const total = (r.checklist || []).length;
                const pct = total ? Math.round((done / total) * 100) : 0;
                const du = r.joining_date ? daysUntil(r.joining_date) : null;
                return (
                  <tr key={r.id} className="border-t hover:bg-zinc-50 cursor-pointer" onClick={() => openDetail(r)} data-testid={`pb-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-zinc-900">{r.candidate_name}</div>
                      <div className="text-xs text-zinc-500">{r.candidate_email}</div>
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{r.requisition_title || "—"}</td>
                    <td className="px-4 py-3">
                      {r.joining_date ? (
                        <div>
                          <div className="text-zinc-700">{fmtDate(r.joining_date)}</div>
                          {du != null && (
                            <div className={`text-xs ${du < 0 ? "text-rose-600" : du <= 7 ? "text-amber-600" : "text-zinc-500"}`}>
                              {du < 0 ? `${-du}d ago` : du === 0 ? "Today" : `in ${du}d`}
                            </div>
                          )}
                        </div>
                      ) : <Badge variant="outline" className="text-rose-600 border-rose-300">DOJ pending</Badge>}
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{r.buddy_name || <span className="text-rose-600 text-xs">Unassigned</span>}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-zinc-200 rounded-full h-1.5">
                          <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-zinc-600 whitespace-nowrap">{done}/{total}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={r.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-200 text-zinc-700"}>
                        {r.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openDetail(r); }} data-testid={`pb-open-${r.id}`}>
                        Open
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      </div>

      {viewing && (
        <DetailDialog
          row={viewing}
          employees={employees}
          onClose={() => setViewing(null)}
          onUpdated={(d) => { setViewing(d); load(); }}
        />
      )}
    </AppShell>
  );
}

function KPI({ label, value, tone = "zinc", testid }) {
  const palette = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
    zinc:    "bg-white border-zinc-200 text-zinc-900",
  }[tone];
  return (
    <Card className={`p-4 ${palette}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-3xl font-bold mt-1">{value}</div>
    </Card>
  );
}

function DetailDialog({ row, employees, onClose, onUpdated }) {
  const [doj, setDoj] = useState(row.joining_date || "");
  const [buddyId, setBuddyId] = useState(row.buddy_employee_id || "");
  const [buddyIntro, setBuddyIntro] = useState(row.buddy_intro || "");
  const [welcomeMd, setWelcomeMd] = useState(row.welcome_message_md || "");
  const [newSlot, setNewSlot] = useState({ time: "", title: "", location: "", owner_name: "" });
  const [saving, setSaving] = useState(false);
  const portalUrl = `${window.location.origin}/preboarding/${row.magic_token}`;

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (doj !== (row.joining_date || "")) body.joining_date = doj || null;
      if (buddyId !== (row.buddy_employee_id || "")) body.buddy_employee_id = buddyId || null;
      if (buddyIntro !== (row.buddy_intro || "")) body.buddy_intro = buddyIntro;
      if (welcomeMd !== (row.welcome_message_md || "")) body.welcome_message_md = welcomeMd;
      if (Object.keys(body).length === 0) {
        toast.info("Nothing to save.");
        setSaving(false);
        return;
      }
      const r = await api.patch(`/preboarding/${row.id}`, body);
      toast.success("Saved.");
      onUpdated(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleChecklist = async (item, nextStatus) => {
    try {
      const r = await api.post(`/preboarding/${row.id}/checklist/${item.id}/toggle`, { status: nextStatus });
      onUpdated(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addSlot = async () => {
    if (!newSlot.time || !newSlot.title) { toast.error("Time and title required."); return; }
    try {
      const r = await api.post(`/preboarding/${row.id}/schedule`, newSlot);
      onUpdated(r.data);
      setNewSlot({ time: "", title: "", location: "", owner_name: "" });
      toast.success("Slot added.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeSlot = async (slotId) => {
    try {
      const r = await api.delete(`/preboarding/${row.id}/schedule/${slotId}`);
      onUpdated(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendDrip = async (dripId) => {
    try {
      await api.post(`/preboarding/${row.id}/send-drip/${dripId}`);
      toast.success("Drip queued.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const resendLink = async () => {
    try {
      const r = await api.post(`/preboarding/${row.id}/resend-link`);
      toast.success(`Magic link reissued: ${r.data.portal_url}`);
      // reload the row to capture new magic_token
      const fresh = await api.get(`/preboarding/${row.id}`);
      onUpdated(fresh.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(portalUrl);
      toast.success("Portal link copied.");
    } catch { toast.error("Couldn't copy. Long-press the URL to copy manually."); }
  };

  const done = (row.checklist || []).filter((c) => c.status === "done").length;
  const total = (row.checklist || []).length;
  const groupedChecklist = (row.checklist || []).reduce((acc, c) => {
    acc[c.owner] = acc[c.owner] || [];
    acc[c.owner].push(c);
    return acc;
  }, {});

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="pb-detail-dialog">
        <DialogHeader>
          <DialogTitle>{row.candidate_name} — Pre-onboarding</DialogTitle>
          <DialogDescription>
            {row.requisition_title} · Joining {row.joining_date ? fmtDate(row.joining_date) : "TBD"}
            {row.reserved_employee_code && ` · ${row.reserved_employee_code}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-2">

          {/* Top quick-actions: magic link */}
          <Card className="p-4 bg-indigo-50 border-indigo-200">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-wider text-indigo-800 font-semibold mb-1">Candidate portal link</div>
                <div className="text-xs text-indigo-900 font-mono truncate">{portalUrl}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={copyLink} data-testid="pb-copy-link">
                  <Copy size={14} className="mr-1" /> Copy
                </Button>
                <Button size="sm" variant="outline" onClick={resendLink} data-testid="pb-resend-link">
                  <EnvelopeSimple size={14} className="mr-1" /> Resend welcome
                </Button>
              </div>
            </div>
          </Card>

          {/* Joining date + Buddy + Welcome msg */}
          <Card className="p-4">
            <h3 className="font-semibold text-zinc-900 mb-3">Joining details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Joining date</Label>
                <DateInput value={doj} onChange={(e) => setDoj(e.target.value)} data-testid="pb-doj" />
              </div>
              <div>
                <Label className="text-xs">Onboarding buddy</Label>
                <Select value={buddyId} onValueChange={setBuddyId}>
                  <SelectTrigger data-testid="pb-buddy"><SelectValue placeholder="Pick a buddy from your team…" /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.name} — {e.job_title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Buddy intro (shown to candidate)</Label>
                <Textarea rows={2} value={buddyIntro} onChange={(e) => setBuddyIntro(e.target.value)}
                  placeholder="A short personal note about who their buddy is — e.g., 'Riya has been with us for 3 years, leads the front-end guild and loves climbing.'"
                  data-testid="pb-buddy-intro" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Welcome message (markdown supported)</Label>
                <Textarea rows={3} value={welcomeMd} onChange={(e) => setWelcomeMd(e.target.value)}
                  placeholder="Optional personal welcome from the hiring manager or CEO." data-testid="pb-welcome-md" />
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <Button onClick={save} disabled={saving} data-testid="pb-save">{saving ? "Saving…" : "Save changes"}</Button>
            </div>
          </Card>

          {/* Checklist */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-zinc-900">Checklist <span className="text-sm text-zinc-500 font-normal">({done}/{total} done)</span></h3>
            </div>
            {Object.entries(groupedChecklist).map(([owner, items]) => (
              <div key={owner} className="mb-4">
                <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">{owner}</div>
                <ul className="space-y-1">
                  {items.map((it) => (
                    <li key={it.id} className="flex items-center justify-between py-1 border-b border-zinc-100 last:border-0">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {it.status === "done" ? <CheckCircle size={16} weight="fill" className="text-emerald-500 shrink-0" /> : <div className="w-4 h-4 border border-zinc-300 rounded shrink-0" />}
                        <span className={`text-sm ${it.status === "done" ? "line-through text-zinc-400" : "text-zinc-800"}`}>{it.label}</span>
                        {it.required && <Badge variant="outline" className="text-[10px]">required</Badge>}
                      </div>
                      <div className="flex gap-2 items-center">
                        {it.evidence_filename && <a href={it.evidence_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline">{it.evidence_filename}</a>}
                        <Button size="sm" variant="ghost" onClick={() => toggleChecklist(it, it.status === "done" ? "todo" : "done")} data-testid={`pb-toggle-${it.id}`}>
                          {it.status === "done" ? "Reopen" : "Mark done"}
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </Card>

          {/* Day 1 schedule */}
          <Card className="p-4">
            <h3 className="font-semibold text-zinc-900 mb-3">Day-1 schedule</h3>
            <ul className="space-y-2 mb-3">
              {(row.day_one_schedule || []).sort((a,b)=>(a.time||"").localeCompare(b.time||"")).map((s) => (
                <li key={s.id} className="flex items-center justify-between bg-zinc-50 rounded p-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="text-sm font-mono font-semibold text-zinc-900 w-12">{s.time}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-900">{s.title}</div>
                      <div className="text-xs text-zinc-500">{[s.location, s.owner_name].filter(Boolean).join(" · ")}</div>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeSlot(s.id)} data-testid={`pb-remove-slot-${s.id}`}>
                    <Trash size={14} />
                  </Button>
                </li>
              ))}
            </ul>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
              <div>
                <Label className="text-xs">Time</Label>
                <Input type="time" value={newSlot.time} onChange={(e) => setNewSlot({ ...newSlot, time: e.target.value })} data-testid="pb-newslot-time" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Activity</Label>
                <Input value={newSlot.title} onChange={(e) => setNewSlot({ ...newSlot, title: e.target.value })} data-testid="pb-newslot-title" />
              </div>
              <div>
                <Label className="text-xs">Location</Label>
                <Input value={newSlot.location} onChange={(e) => setNewSlot({ ...newSlot, location: e.target.value })} data-testid="pb-newslot-loc" />
              </div>
              <Button onClick={addSlot} data-testid="pb-add-slot"><Plus size={14} className="mr-1" /> Add</Button>
            </div>
          </Card>

          {/* Drip log */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-zinc-900">Email drips</h3>
              <p className="text-xs text-zinc-500">Auto-fires on schedule, but you can re-send anytime.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {DRIPS.map((d) => {
                const log = (row.drip_log || []).filter((l) => l.sequence_id === d.id);
                const last = log[log.length - 1];
                return (
                  <div key={d.id} className="flex items-center justify-between bg-zinc-50 rounded p-2">
                    <div>
                      <div className="text-sm text-zinc-900">{d.label}</div>
                      <div className="text-xs text-zinc-500">
                        {last ? `Last sent ${fmtDate(last.sent_at)}` : "Not sent yet"}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => sendDrip(d.id)} data-testid={`pb-drip-${d.id}`}>
                      <EnvelopeSimple size={14} className="mr-1" /> Send
                    </Button>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="pb-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
